import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

/**
 * Left sidebar listing parts → numbered question pills. Only one part is
 * expanded at a time. Pills are colour-coded by per-question result.
 */
const ReviewSidebar = ({
  theme,
  parts,
  numberMap,
  expandedPart,
  setExpandedPart,
  selectedQuestionId,
  onPickItem,
  variant = "inline",
}) => {
  const getPillStyle = (it) => {
    const isSelected = selectedQuestionId === it.questionId;
    const { answered, isCorrect } = it.item || {};
    const base = {
      backgroundColor: "transparent",
      border: "1px solid",
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
    };
    if (answered) {
      const c = isCorrect
        ? theme.palette.success.main
        : theme.palette.error.main;
      Object.assign(base, {
        backgroundColor: c,
        color: "white",
        borderColor: c,
      });
    }
    if (isSelected) {
      base.boxShadow = theme.shadows[6];
      base.transform = "scale(1.12)";
    }
    return base;
  };

  const isDrawer = variant === "drawer";

  return (
    <Paper
      elevation={0}
      sx={{
        width: isDrawer ? "100%" : 280,
        flexShrink: 0,
        borderRight: isDrawer ? "none" : `1px solid ${theme.palette.divider}`,
        height: isDrawer ? "auto" : "100%",
        overflow: "auto",
        borderRadius: 0,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Parts & Questions
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click a question to view details
        </Typography>
      </Box>
      <Divider />
      {parts.map(({ partNumber, items }) => {
        const correct = items.filter((it) => it.item?.isCorrect).length;
        return (
          <Accordion
            key={partNumber}
            expanded={expandedPart === partNumber}
            onChange={() =>
              setExpandedPart(expandedPart === partNumber ? null : partNumber)
            }
            disableGutters
            square
            sx={{ "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography fontWeight={600}>
                  Part {partNumber || "—"}
                </Typography>
                <Chip
                  size="small"
                  label={`${correct}/${items.length}`}
                  color={correct === items.length ? "success" : "default"}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {items.map((it) => (
                  <Box
                    key={it.questionId}
                    onClick={() => onPickItem(it)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      transition: "all 0.2s",
                      ...getPillStyle(it),
                      "&:hover": {
                        transform: "scale(1.15)",
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    {numberMap[it.questionId]}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Paper>
  );
};

export default ReviewSidebar;
